import { Check } from 'lucide-react';
import { WORK_KINDS } from '@/lib/labourState';
import { chipMetrics, chipRowClass, chipButtonClass } from '@/components/ChipRow';
/* ⛔ `WORK_MARK_SPIN_SECONDS` IS DELIBERATELY NOT IMPORTED. Nothing here spins —
   see the header — and `web/tsconfig.json` sets `noUnusedLocals`, so importing it
   "for consistency" would fail the build. ** A CONSTANT IMPORTED AND NOT USED IS A
   CLAIM THE FILE MAKES AND DOES NOT KEEP. ** */
import { WorkTruckMark } from '@/components/WorkTruckMark';

/**
 * ============================================================================
 * ⭐⭐⭐ THE WORK FILTER — LOADING / UNLOADING, BESIDE THE STATUS BAR
 * ============================================================================
 *
 * > Pete, 2026-09-24: *"it would be great to filter the calendar by Unloading
 * > and Loading in the status bar, is it possible to add those?"*
 * > Asked whether to fold them into the status chips or stand them beside:
 * > **"Separate control beside the status bar."**
 *
 * ---- ⛔⛔ WHY IT IS A SECOND CONTROL AND NOT TWO MORE STATUS CHIPS --------
 *
 * A truck being unloaded is `statusCode === 'ARRIVED'`. It is ARRIVED before
 * anybody touches it, while it is being worked, and after — that is what the
 * status means, and `labourState.workStatusLabel` exists precisely because Pete
 * said *"it stays arrived but needs a status of that process."*
 *
 * ⛔ SO PUTTING THEM IN `APPOINTMENT_STATUSES` WOULD HAVE BROKEN THE STATUS BAR,
 * not merely crowded it. `StatusFilterBar` treats its codes as MUTUALLY
 * EXCLUSIVE BUCKETS — it filters `statusFilter.has(a.statusCode)` and counts one
 * load into exactly one chip. An `UNLOADING` chip there would have:
 *   1. matched no `statusCode` at all, so it would filter to nothing; and
 *   2. made *"arrived AND being unloaded"* **unaskable**, because selecting two
 *      chips in that row is an OR.
 *   ** A SECOND QUESTION ABOUT A ROW IS A SECOND AXIS. FOLDING IT INTO THE
 *      FIRST DOES NOT CROWD THE FIRST, IT MAKES THE INTERSECTION UNREACHABLE. **
 *
 * ⭐ Beside it, the two ANDs: status picks WHICH TRUCKS, work picks WHAT IS
 * HAPPENING TO THEM. `Calendar.tsx` applies them in that order.
 *
 * ---- ⭐ WHY IT LOOKS EXACTLY LIKE THE STATUS BAR --------------------------
 *
 * Same `chipMetrics` / `chipRowClass` / `chipButtonClass`, same `dense`, same
 * tick-in-a-swatch affordance. ⛔ NOT matched by eye — it imports the same three
 * functions `StatusFilterBar` does, which is the only version of "consistent"
 * that survives the next change to either row (§1AV.170).
 *
 * ⚠️ AND THE SWATCH IS THE ORBITING TRUCK, NOT A COLOUR. The status chips can
 * use colour because each names a colour drawn on the grid; there is no tile
 * colour for "being worked" — there is a GLYPH, and it is `WorkTruckMark`, the
 * same mark the tile, the wall board, the driver card and the party card wear.
 *   ** A KEY MUST LOOK LIKE THE THING IT NAMES. A COLOUR INVENTED FOR A KEY IS
 *      A KEY TO NOTHING. **
 * ⛔ The mark does NOT spin in the OFF state. Motion is what it means; a row of
 * spinning marks nobody selected would read as four trucks being worked.
 *
 * ---- ⛔⛔ EMPTY MEANS "NO FILTER", EXACTLY AS IT DOES NEXT DOOR -----------
 *
 * Both chips off shows everything, and that is the zero state — NOT "show
 * nothing". ⚠️ There is deliberately no "All Work" chip: with two options, both
 * off and both on draw the same board, so a third control would be a second way
 * to reach a state already one click away.
 */
export interface WorkFilterBarProps {
    /** Codes currently selected. ⛔ EMPTY MEANS "no filter", not "show nothing". */
    workFilter: Set<string>;
    /** Toggle one code. Owned by `Calendar.tsx`, like `toggleStatus`. */
    toggleWork: (code: string) => void;
    /**
     * ⭐ Per-code counts over the **UNFILTERED** range — the same rule
     * `StatusFilterBar` documents at length. ⛔ A count computed from what
     * survived the filter cannot tell you what the filter is hiding, which is
     * the only question it is on screen to answer.
     */
    workCounts: Record<string, number>;
    /** Tighter sizing for the board header strip. */
    dense?: boolean;
    className?: string;
}

export function WorkFilterBar({
    workFilter,
    toggleWork,
    workCounts,
    dense = false,
    className,
}: WorkFilterBarProps) {
    const { swatch, tick, ring } = chipMetrics(dense);
    /* ⚠️ `tick` IS READ, and that is not decoration: without a mark that differs
       between on and off, a selected chip and an unselected one differ only by a
       hairline ring — a difference you have to already know to see. Pete asked for
       exactly this on the status row: *"need check marks in the little circles ...
       so you know which ones are selected"*. ⭐ Here the ON state shows the TICK and
       the OFF state shows the TRUCK, so the chip always carries a glyph and the row
       never changes width as it is used. */
    return (
        <div className={chipRowClass('status', dense, className)}>
            {WORK_KINDS.map(({ code, label }) => {
                const checked = workFilter.has(code);
                return (
                    <button
                        key={code}
                        type="button"
                        onClick={() => toggleWork(code)}
                        title={checked
                            ? `Stop filtering by ${label.toLowerCase()}`
                            /* ⛔ THE TOOLTIP STATES THE PREDICATE, because the chip cannot.
                               "Loading" on a two-word chip does not say whether a paused
                               truck is in or out, and that is the one thing somebody will
                               want to know the first time the board surprises them. */
                            : `Show only appointments being ${label.toLowerCase()} (paused counts)`}
                        className={chipButtonClass('status', dense,
                            checked ? 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100' : 'hover:bg-muted')}
                    >
                        {/**
                          * ---- ⛔⚤ ~~`text-muted-foreground/70`~~ — 2026-09-25 ------------
                          *
                          * > Pete: *"Maybe make the little icons green even when not
                          * > checkmarked to match the status format then they dont look
                          * > disabled."*
                          *
                          * ⛔ HE DIAGNOSED IT EXACTLY. The off state was muted AND at 70%
                          * opacity - two dimming devices stacked on one glyph. Measured on
                          * this surface that lands near 2.3:1, which is below the floor for
                          * ANY text, let alone a control somebody is meant to press.
                          *
                          * ⚠️ AND DIMMING WAS NEVER THE RIGHT SIGNAL HERE. An unchecked
                          * filter is not unavailable - it is the MOST available thing on the
                          * row, because pressing it is the entire point. The state being
                          * shown is "not applied", and greying is how this app says
                          * "cannot be used".
                          *   ** WHEN THE SAME VISUAL DEVICE MEANS BOTH NOT-SELECTED AND
                          *      NOT-AVAILABLE, EVERY UNSELECTED CONTROL LOOKS BROKEN. **
                          *
                          * ⭐ SELECTION IS NOW CARRIED BY FILL, NOT BY BRIGHTNESS: on is a
                          * solid emerald disc with a white tick, off is the same emerald
                          * drawn as a truck on no fill. Both are legible; only one is full.
                          */}
                        <span
                            className={`inline-flex ${swatch} items-center justify-center rounded-full ${
                                checked
                                    ? `bg-emerald-600 text-white ${ring}`
                                    : 'text-emerald-600'
                            }`}
                        >
                            {checked
                                ? <Check className={tick} strokeWidth={3} />
                                : (
                                    /* ⚠️ STILL, NOT SPINNING — see the header. */
                                    <WorkTruckMark
                                        wrapClassName="relative inline-flex h-full w-full"
                                        ringClassName="absolute inset-0 h-full w-full"
                                        truckClassName="absolute left-[23%] top-[23%] h-[54%] w-[54%]"
                                        ringStrokeWidth={4.5}
                                        truckStrokeWidth={2.6}
                                    />
                                )}
                        </span>
                        {label}{dense
                            ? <span className="tabular-nums">{workCounts[code] ?? 0}</span>
                            : ` (${workCounts[code] ?? 0})`}
                    </button>
                );
            })}
        </div>
    );
}

export default WorkFilterBar;
